// Single source of truth for the AFF entities' columns. The frontend grid
// (affExplorer configs) and the backend CSV export (ENTITY_CONFIGS.aff_* in
// exportService) both derive from these arrays, so adding or renaming a column
// happens in exactly one place.
//
// Time semantics (#779): aff_tracks carries two distinct facts and they must
// stay visibly distinct in every surface —
//   position_timestamp = when the observation happened  → "Observed"
//   created_at         = when the record reached the DB → "Captured"
// They routinely differ by days, so a single ambiguous "Date" column would be
// actively misleading.

import type { ColumnSchema } from './columnSchema.js';
import { MS_TO_KNOTS, METRES_TO_FEET } from './affUnits.js';

// Units (#810): speed is stored in metres per second and altitude in metres
// above sea level, for all three reporting providers. The Aviation group asked
// the display to show knots and feet above sea level, so the converted values
// are virtual columns computed in SQL rather than a reinterpretation of what is
// stored. The raw columns stay — hidden from the grid, still carried by
// exports — so the database remains the single unit of record.
//
// The factor lives in affUnits and is interpolated here, so SQL and TypeScript
// cannot drift apart.
const SPEED_KNOTS_COL: ColumnSchema = {
  field: 'speedKnots', dbCol: `round(speed * ${MS_TO_KNOTS})`, title: 'Speed (kn)', filterable: true,
};
const ALT_FEET_COL: ColumnSchema = {
  field: 'altFeet', dbCol: `round(alt * ${METRES_TO_FEET})`, title: 'Altitude (ft ASL)', filterable: true,
};

export const AFF_AIRCRAFT_COLUMNS: ColumnSchema[] = [
  { field: 'name',                dbCol: 'name',                 title: 'Tail',            filterable: true,  order: 1 },
  { field: 'esn',                 dbCol: 'esn',                  title: 'ESN/IMEI',             filterable: true,  order: 2 },
  // #798 — both arrive from Selkirk and were discarded before storage until the
  // collector change. The airframe is abbreviated for display (FW/RW) by
  // airframeCode; the column carries what was reported.
  { field: 'callsign',            dbCol: 'callsign',             title: 'Call Sign',       filterable: true,  order: 2.5 },
  { field: 'airframe',            dbCol: 'resource_subclass',    title: 'Type',            filterable: true,  order: 2.6 },
  { field: 'make',                dbCol: 'make',                 title: 'Make',            filterable: true,  order: 3 },
  { field: 'model',               dbCol: 'model',                title: 'Model',           filterable: true,  order: 4 },
  { field: 'owner',               dbCol: 'owner',                title: 'Owner',           filterable: true,  order: 5 },
  { field: 'operationalFunction', dbCol: 'operational_function', title: 'Function',        filterable: true,  order: 6 },
  { field: 'groupDesignator',     dbCol: 'group_designator',     title: 'Group',           filterable: true,  order: 7 },
  { field: 'lat',                 dbCol: 'lat',                  title: 'Latitude',        filterable: true,  order: 8 },
  { field: 'lon',                 dbCol: 'lon',                  title: 'Longitude',       filterable: true,  order: 9 },
  { field: 'alt',                 dbCol: 'alt',                  title: 'Altitude (m ASL)', filterable: true,  hiddenInGrid: true, order: 10 },
  { ...ALT_FEET_COL,                                                                                            order: 10.5 },
  { field: 'speed',               dbCol: 'speed',                title: 'Speed (m/s)',     filterable: true,  hiddenInGrid: true, order: 11 },
  { ...SPEED_KNOTS_COL,                                                                                          order: 11.5 },
  { field: 'heading',             dbCol: 'heading',              title: 'Heading',         filterable: true,  order: 12, mobile: { hidden: true } },
  { field: 'positionTimestamp',   dbCol: 'position_timestamp',   title: 'Observed',        filterable: true,  order: 13 },
  { field: 'recordAgeMins',       dbCol: 'record_age_mins',      title: 'Age (min)',       filterable: true,  order: 14 },
  { field: 'stale',               dbCol: 'stale',                title: 'Stale',           filterable: true,  order: 15, mobile: { hidden: true } },
  { field: 'source',              dbCol: 'source',               title: 'Source',          filterable: true,  order: 16, mobile: { hidden: true } },
  // last_connect / last_collect deliberately absent: they are bumped only on
  // the heartbeat row (aff_service_2026 migration 002, #440) and are NULL for
  // every real aircraft, which the Aircraft context is made of.
  { field: 'createdAt',           dbCol: 'created_at',           title: 'Captured',        filterable: true,  hiddenInGrid: true, order: 19 },
];

export const AFF_TRACK_COLUMNS: ColumnSchema[] = [
  { field: 'name',              dbCol: 'name',               title: 'Tail',      filterable: true,  order: 1 },
  { field: 'callsign',          dbCol: 'callsign',           title: 'Call Sign', filterable: true,  order: 1.5 },
  { field: 'esn',               dbCol: 'esn',                title: 'ESN/IMEI',       filterable: true,  order: 2 },
  { field: 'positionTimestamp', dbCol: 'position_timestamp', title: 'Observed',  filterable: true,  order: 3 },
  { field: 'lat',               dbCol: 'lat',                title: 'Latitude',  filterable: true,  order: 4 },
  { field: 'lon',               dbCol: 'lon',                title: 'Longitude', filterable: true,  order: 5 },
  { field: 'alt',               dbCol: 'alt',                title: 'Altitude (m ASL)', filterable: true, hiddenInGrid: true, order: 6 },
  { ...ALT_FEET_COL,                                                                                     order: 6.5 },
  { field: 'speed',             dbCol: 'speed',              title: 'Speed (m/s)', filterable: true, hiddenInGrid: true, order: 7 },
  { ...SPEED_KNOTS_COL,                                                                                  order: 7.5 },
  { field: 'heading',           dbCol: 'heading',            title: 'Heading',   filterable: true,  order: 8, mobile: { hidden: true } },
  { field: 'createdAt',         dbCol: 'created_at',         title: 'Captured',  filterable: true,  order: 10 },
  // #866 — the aircraft each point was flown in, from the aircraft history
  // period covering the point (see affTrackSource). NULL where no period
  // covers it. Not what the tracker is fitted to now: a tracker moved to
  // another aircraft keeps its old flights under the old aircraft.
  { field: 'make',                dbCol: 'make',                 title: 'Make',     filterable: true, order: 11 },
  { field: 'model',               dbCol: 'model',                title: 'Model',    filterable: true, order: 12 },
  { field: 'owner',               dbCol: 'owner',                title: 'Owner',    filterable: true, order: 13 },
  { field: 'operationalFunction', dbCol: 'operational_function', title: 'Function', filterable: true, order: 14 },
  { field: 'resourceSubclass',    dbCol: 'resource_subclass',    title: 'Subclass', filterable: true, order: 15, mobile: { hidden: true } },
];

/**
 * The derived unit columns sort by the stored value they are computed from:
 * the same order, and the plain column can use an index.
 */
const DERIVED_SORT: Record<string, string> = { speedKnots: 'speed', altFeet: 'alt' };

const sortColumnsFor = (schema: ColumnSchema[], extra: Record<string, string> = {}): Readonly<Record<string, string>> => ({
  ...Object.fromEntries(schema.map((c) => [c.field, DERIVED_SORT[c.field] ?? c.dbCol])),
  ...extra,
});

/**
 * Sort whitelists, sortBy -> SQL column (#866 U2): every column each grid
 * shows, by its schema field. The router refuses any other sortBy with 400 and
 * the Explorer shows a sort arrow only on a field listed here, so the two read
 * the same list and cannot drift.
 */
export const AFF_AIRCRAFT_SORT_COLUMNS = sortColumnsFor(AFF_AIRCRAFT_COLUMNS, {
  // The grid calls it airframe; the stored name is accepted too.
  resourceSubclass: 'resource_subclass',
});
export const AFF_TRACK_SORT_COLUMNS = sortColumnsFor(AFF_TRACK_COLUMNS);

/** Whether a sortBy is in a whitelist (own keys only, never the prototype). */
export const affSortable = (columns: Readonly<Record<string, string>>, sortBy: string): boolean =>
  Object.prototype.hasOwnProperty.call(columns, sortBy);
